// Public-facing signing page — no auth. Signer opens /sign/:token and completes.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ShieldCheck, CheckCircle2, XCircle, ArrowRight, ArrowDown, Download } from "lucide-react";
import SignatureCanvas from "./SignatureCanvas";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", env: null, signer: null, fields: [] });
  const [values, setValues] = useState({}); // fieldId → string
  const [signaturePng, setSignaturePng] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfSrc, setPdfSrc] = useState(null);
  const activeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public/sign/${token}`)
      .then((r) => {
        setState({ loading: false, error: "", env: r.data.envelope, signer: r.data.signer, fields: r.data.fields });
        setPdfSrc(`data:application/pdf;base64,${r.data.envelope.doc_b64}`);
        // seed values with defaults (dates prefill to today)
        const init = {};
        for (const f of r.data.fields) {
          if (f.type === "date" && !f.value) init[f.id] = new Date().toLocaleDateString("en-CA");
          else if (f.value !== null && f.value !== undefined) init[f.id] = String(f.value);
        }
        setValues(init);
      })
      .catch((err) => setState({ loading: false, error: err.response?.data?.detail || "Could not load", env: null, signer: null, fields: [] }));
  }, [token]);

  const requiredFields = useMemo(() => state.fields, [state.fields]);
  const needsSignature = useMemo(() =>
    state.fields.some((f) => f.type === "signature" || f.type === "initial"),
    [state.fields]);

  const canSubmit = useMemo(() => {
    if (needsSignature && !signaturePng) return false;
    for (const f of requiredFields) {
      if (!f.required) continue;
      if (f.type === "signature" || f.type === "initial") continue;
      if (f.type === "checkbox") continue;
      if (!values[f.id]) return false;
    }
    return true;
  }, [requiredFields, values, needsSignature, signaturePng]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/sign/${token}/submit`, {
        field_values: values,
        signature_png: signaturePng,
        typed_name: state.signer.name,
      });
      setDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (state.loading) return <FullBleed>Loading document…</FullBleed>;
  if (state.error) return <FullBleed><XCircle size={20} className="mx-auto mb-2" style={{ color: "#ff453a" }} />{state.error}</FullBleed>;

  if (done || state.signer.status === "signed") {
    return (
      <FullBleed>
        <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: "#30d158" }} />
        <div className="text-[22px] mb-1" style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>Thank you — your signature is recorded.</div>
        <div className="text-[13px]" style={{ color: "var(--crm-text-muted)" }}>
          A cryptographically-sealed Certificate of Completion will be produced once every signer finishes.
        </div>
      </FullBleed>
    );
  }

  return (
    <div className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #f7f7fa 0%, #eef1f7 100%)",
        color: "#0a1230",
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", system-ui, sans-serif',
      }}>
      {/* Top bar */}
      <header className="sticky top-0 z-10 flex items-center gap-4 px-6 h-14"
        style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
        <img src="/prospera-icon.png" alt="" className="w-7 h-7 rounded-full" style={{ boxShadow: "0 0 0 1px rgba(183,138,47,0.4)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] truncate" style={{ fontWeight: 600 }}>{state.env.subject}</div>
          <div className="text-[10.5px] font-mono truncate" style={{ color: "rgba(10,18,48,0.55)" }}>
            {state.env.doc_hash?.slice(0, 24)}…
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-full"
          style={{ background: "rgba(48,209,88,0.08)", color: "#1e7e34", border: "1px solid rgba(48,209,88,0.25)" }}>
          <ShieldCheck size={12} /> Ed25519 sealed
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px]" style={{ minHeight: "calc(100vh - 56px)" }}>
        {/* PDF preview */}
        <main className="p-6 overflow-y-auto">
          {pdfSrc && (
            <Document file={pdfSrc} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => (
                <div key={pn} className="mx-auto mb-6 relative shadow-xl rounded-md overflow-hidden"
                  style={{ maxWidth: "100%", width: "fit-content", background: "#fff" }}>
                  <Page pageNumber={pn} width={780} renderAnnotationLayer={false} renderTextLayer={false} />
                  <FieldsOverlay
                    fields={state.fields.filter((f) => f.page === pn)}
                    values={values} setValues={setValues}
                    signaturePng={signaturePng}
                    activeRef={activeRef}
                  />
                </div>
              ))}
            </Document>
          )}
        </main>

        {/* Sidebar */}
        <aside className="border-l p-6 space-y-5"
          style={{ background: "rgba(255,255,255,0.7)", borderColor: "rgba(0,0,0,0.06)" }}>
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "rgba(10,18,48,0.5)" }}>Signing as</div>
            <div className="text-[15px] mt-1" style={{ fontWeight: 600 }}>{state.signer.name}</div>
            <div className="text-[12px]" style={{ color: "rgba(10,18,48,0.55)" }}>{state.signer.email}</div>
          </div>

          {state.env.message && (
            <div className="rounded-xl p-3 text-[12.5px]"
              style={{ background: "rgba(0,113,227,0.05)", border: "1px solid rgba(0,113,227,0.15)", color: "#0a1230" }}>
              {state.env.message}
            </div>
          )}

          {needsSignature && (
            <div>
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "rgba(10,18,48,0.5)" }}>Your signature</div>
              <SignatureCanvas onChange={setSignaturePng} defaultName={state.signer.name} />
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "rgba(10,18,48,0.5)" }}>
              Fields ({state.fields.length})
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {state.fields.map((f, idx) => (
                <div key={f.id} className="flex items-center gap-2 text-[12px] px-2 py-1.5 rounded-md"
                  style={{ background: (values[f.id] || (f.type === "signature" && signaturePng)) ? "rgba(48,209,88,0.08)" : "rgba(0,0,0,0.03)" }}>
                  {(values[f.id] || (f.type === "signature" && signaturePng)) ? (
                    <CheckCircle2 size={12} style={{ color: "#30d158" }} />
                  ) : <ArrowDown size={12} style={{ color: "rgba(10,18,48,0.45)" }} />}
                  <span style={{ color: "#0a1230" }}>{f.type}</span>
                  <span className="ml-auto text-[10.5px]" style={{ color: "rgba(10,18,48,0.5)" }}>p.{f.page}</span>
                </div>
              ))}
            </div>
          </div>

          <button onClick={submit} disabled={!canSubmit || busy} data-testid="submit-signature"
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[13.5px] font-medium text-black disabled:opacity-40"
            style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}>
            {busy ? "Submitting…" : "Finish signing"} <ArrowRight size={14} />
          </button>

          <div className="text-[10.5px] leading-relaxed" style={{ color: "rgba(10,18,48,0.55)" }}>
            By clicking Finish signing, you agree that your electronic signature and each field entry are legally binding.
            All actions are timestamped, hashed with SHA-256 and sealed by Ed25519.
          </div>
        </aside>
      </div>
    </div>
  );
}

function FieldsOverlay({ fields, values, setValues, signaturePng }) {
  return (
    <>
      {fields.map((f) => {
        const filled = f.type === "signature" || f.type === "initial" ? !!signaturePng : !!values[f.id];
        const bg = filled ? "rgba(48,209,88,0.10)" : "rgba(0,113,227,0.10)";
        const border = filled ? "rgba(48,209,88,0.6)" : "rgba(0,113,227,0.55)";
        return (
          <div key={f.id}
            data-testid={`overlay-field-${f.id}`}
            className="absolute"
            style={{
              left: `${f.x_ratio * 100}%`, top: `${f.y_ratio * 100}%`,
              width: `${f.w_ratio * 100}%`, height: `${f.h_ratio * 100}%`,
              background: bg, border: `1.5px dashed ${border}`, borderRadius: 3,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
            {f.type === "signature" || f.type === "initial" ? (
              signaturePng ? (
                <img src={signaturePng} alt="" className="object-contain w-full h-full" style={{ padding: 2 }} />
              ) : (
                <span className="text-[10px]" style={{ color: border }}>{f.type}</span>
              )
            ) : f.type === "checkbox" ? (
              <input type="checkbox" checked={values[f.id] === "true"}
                onChange={(e) => setValues({ ...values, [f.id]: e.target.checked ? "true" : "false" })}
                className="w-full h-full" style={{ accentColor: "#0071e3" }}
              />
            ) : (
              <input
                value={values[f.id] || ""}
                onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                type={f.type === "date" ? "date" : "text"}
                placeholder={f.type}
                className="w-full h-full bg-transparent px-1 text-[11px] focus:outline-none"
                style={{ color: "#0a1230" }}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function FullBleed({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "linear-gradient(180deg, #f7f7fa 0%, #eef1f7 100%)", color: "#0a1230", fontFamily: '-apple-system, sans-serif' }}>
      <div className="text-center max-w-md">{children}</div>
    </div>
  );
}
